import React, { useEffect, useRef } from 'react'
import { useMessaging, formatTimeDiff } from './MessagingProvider'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { cn } from '~/lib/utils'
import safeParse from '~/lib/safeParse'
import useTranslation from '../shared/TranslationProvider'

const supportMessagesPath = '/support_messages'

const SentList = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { sentMessageId, candidateSearch, sent_messages_path } = useMessaging()
  const sentPath = sent_messages_path || supportMessagesPath
  const { error, data: sentMessages } = useQuery({
    queryKey: ['sentMessages', sentPath],
    queryFn: () => apiGet(`${sentPath}.json`).then((res) => res.data),
    refetchInterval: 10000,
  })
  const filteredMessages = sentMessages?.filter((message) =>
    (message.company_name || '')
      .toLowerCase()
      .includes(candidateSearch.toLowerCase()),
  )

  useEffect(() => {
    if (filteredMessages?.length === 0 && sentMessageId) {
      window.location.href = '#inbox'
    }
  }, [filteredMessages?.length, sentMessageId])

  if (error) return t('an_error_occurred') + error.message

  return (
    <div className="flex flex-col gap-2 p-4 pt-0">
      {filteredMessages?.length === 0 && (
        <div className="flex h-full text-center text-sm">
          {t('sent_messages_appear_here')}
        </div>
      )}
      {filteredMessages?.map((message) => (
        <SentMessageButton key={message.id} message={message} />
      ))}
    </div>
  )
}
export default SentList

const selectedRowLinkClass =
  '!text-white hover:!text-white focus:!text-white hover:!underline focus:!underline'

const SentMessageButton = ({ message }) => {
  const { sentMessageId, currentThreadId, userIsCoach, locale } = useMessaging()
  const buttonRef = useRef(null)
  const { t } = useTranslation('CoachMessageThread')
  const {
    created_at: sentAt,
    id: messageId,
    coach_message_thread_id: threadId,
    subject,
    recipient_full_name: otherUserName,
    link_to_candidate: linkToCandidate,
    company_name: companyName,
  } = message
  const isSelected =
    messageId === sentMessageId && parseInt(threadId || 0) === currentThreadId

  useEffect(() => {
    if (isSelected && buttonRef.current) {
      buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isSelected, threadId, messageId])

  return (
    <div
      ref={buttonRef}
      className={cn(
        'flex cursor-pointer flex-col items-start gap-2 rounded-3xl border border-silver px-6 py-4 text-left text-sm text-intoo-text transition-all hover:bg-intoo-blue-medium-50 hover:text-intoo-text focus:text-intoo-text',
        isSelected &&
          'bg-intoo-blue-medium text-white hover:bg-intoo-blue-medium-900 hover:text-white focus:text-white',
      )}
      role="link"
      tabIndex={0}
      onClick={() => {
        window.location.hash = `#${threadId || 0}-${messageId}`
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          window.location.hash = `#${threadId || 0}-${messageId}`
        }
      }}
    >
      <div className="flex w-full flex-col gap-1">
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold">
              {t('to_line')}{' '}
              {userIsCoach ? (
                <a
                  href={linkToCandidate}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(isSelected && selectedRowLinkClass)}
                >
                  {otherUserName}
                </a>
              ) : (
                otherUserName
              )}
              {companyName && (
                <div className="text-sm font-normal text-mine-shaft-500">
                  {companyName}
                </div>
              )}
            </div>
          </div>
          <div className="ml-auto text-xs">
            {formatTimeDiff(sentAt, locale)}
          </div>
        </div>
        <div className="text-xs font-medium">{subject}</div>
      </div>
    </div>
  )
}

export const SentMessage = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { sentMessageId, currentThreadId, userIsCoach, sent_messages_path } =
    useMessaging()
  const sentPath = sent_messages_path || supportMessagesPath

  const { error, data: sentMessages } = useQuery({
    queryKey: ['sentMessages', sentPath],
    queryFn: () => apiGet(`${sentPath}.json`).then((res) => res.data),
    refetchInterval: 10000,
  })
  if (error) {
    console.error('Error fetching sent messages:', error)
    return t('an_error_occurred')
  }

  const currentSentMessage = sentMessages?.find(
    (message) =>
      message.id === sentMessageId &&
      parseInt(message.coach_message_thread_id || 0) == currentThreadId,
  )

  const {
    recipient_full_name: userFullName,
    subject,
    body,
    attachment = {},
    attachment_filename: attachmentFilename,
    link_to_candidate: linkToCandidate,
  } = currentSentMessage || {}

  const { url: attachmentUrl } = attachment

  if (!(subject || body)) return null

  return (
    <div className="flex h-full flex-col overflow-y-hidden rounded-3xl border border-silver bg-white p-4">
      <div className="relative -mx-4 -mt-4 flex items-center justify-between border-b bg-intoo-blue-medium-100 px-6 py-4">
        <div className="font-roboto-slab text-intoo-blue-medium md:text-2xl">
          {t('subject_line')} {subject}
        </div>
      </div>
      <div className="flex flex-col overflow-y-scroll py-2">
        <div className="py-">
          {t('to_line')}{' '}
          {userIsCoach ? (
            <a href={linkToCandidate} target="_blank" rel="noreferrer">
              {userFullName}
            </a>
          ) : (
            userFullName
          )}
        </div>
        <div className="min-w-0 overflow-hidden [overflow-wrap:anywhere]">
          {safeParse((body || '').replace(/&nbsp;/g, ' '))}
          {attachmentUrl && (
            <div className="my-2">
              <a
                rel="noreferrer"
                href={attachmentUrl}
                className="inline-block"
                download
              >
                {attachmentFilename}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
